import React, { useState, useCallback, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Fade,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Fab,
} from '@mui/material';
import { MyLocation as MyLocationIcon } from '@mui/icons-material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { entriesAPI } from '../services/api';
import { CreateEntryData, TravelEntry } from '../types';
import CreateEntryDialog from '../components/CreateEntryDialog';
import { format, parseISO, isValid } from 'date-fns';
import { backgroundStyles, componentStyles } from '../theme/fojournTheme';
import UniversalMap from '../components/maps/UniversalMap';
import { UniversalMapEntry, UniversalMapMarker, LatLng } from '../components/maps/MapInterface';
import { US_STATES } from '../utils/usStates';

// Helper function to get today's date in YYYY-MM-DD format
const getTodayString = (): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper function to safely format dates
const safeFormatDate = (dateString: string | Date, formatString: string = 'MMM dd, yyyy'): string => {
  try {
    if (!dateString) return 'Unknown date';
    
    let date: Date;
    if (typeof dateString === 'string') {
      date = parseISO(dateString);
      if (!isValid(date)) {
        date = new Date(dateString);
      }
    } else {
      date = dateString;
    }
    
    if (!isValid(date)) {
      return 'Invalid date';
    }
    
    return format(date, formatString);
  } catch (error) {
    return 'Date error';
  }
};

// Helper function to convert TravelEntry to UniversalMapEntry
const convertTravelEntriesToMapEntries = (entries: TravelEntry[]): UniversalMapEntry[] => {
  return entries.map(entry => {
    const mapEntry = {
      id: entry.id,
      title: entry.title || entry.description?.substring(0, 50) || 'Untitled Entry',
      location: {
        lat: Number(entry.latitude),
        lng: Number(entry.longitude)
      },
      locationName: entry.locationName,
      content: entry.description,
      date: entry.createdAt
    };
    return mapEntry;
  });
};

const MapView: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedLocation, setSelectedLocation] = useState<LatLng | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [entryData, setEntryData] = useState({
    locationName: '',
    entryDate: getTodayString(),
  });

  // Check if we're coming from journey planner with memory data
  const locationState = location.state as any;
  const shouldCreateMemory = locationState?.createMemory;
  const memoryData = locationState?.memoryData;

  // Fetch all travel entries
  const { data: entriesResponse, isLoading, error } = useQuery({
    queryKey: ['entries'],
    queryFn: () => entriesAPI.getEntries(),
  });

  // Create entry mutation that navigates to the entry detail page after creation
  const createEntryMutation = useMutation({
    mutationFn: (data: CreateEntryData) => entriesAPI.createEntry(data),
    onSuccess: (response) => {
      // Navigate to the entry detail page and open edit dialog
      navigate(`/entry/${response.data.entry.id}`, { 
        state: { 
          openEditDialog: true,
          from: 'map'
        } 
      });
    },
    onError: (error: any) => {
      console.error('Failed to create memory entry:', error);
    },
  });

  // Auto-create memory if coming from journey planner
  React.useEffect(() => {
    if (shouldCreateMemory && memoryData && !createEntryMutation.isPending) {
      createEntryMutation.mutate({
        title: memoryData.title || 'Memory from Journey',
        description: memoryData.description || '',
        latitude: memoryData.latitude,
        longitude: memoryData.longitude,
        locationName: memoryData.locationName || '',
        entryDate: memoryData.date || getTodayString(),
        memoryType: memoryData.memoryType || 'experience',
        isDogFriendly: memoryData.isDogFriendly || false,
        restaurantRating: memoryData.restaurantRating || undefined,
      });
    }
  }, [shouldCreateMemory, memoryData, createEntryMutation]);

  const entries = entriesResponse?.data?.entries || [];

  // Map configuration - make these stateful so search can update them
  const [center, setCenter] = useState<LatLng>({ lat: 39.8282, lng: -98.5795 }); // Center of USA
  const [zoom, setZoom] = useState(4);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  
  // Convert entries to map entries
  const mapEntries = useMemo(() => convertTravelEntriesToMapEntries(entries), [entries]);
  
  // Create markers for selected location
  const markers: UniversalMapMarker[] = useMemo(() => {
    if (selectedLocation) {
      return [{
        position: selectedLocation,
        title: 'New Memory Location',
        id: 'selected-location'
      }];
    }
    return [];
  }, [selectedLocation]);

  const handleMapClick = useCallback((location: LatLng) => {
    setSelectedLocation(location);
    setDialogOpen(true);
  }, []);

  const handleLocationSearch = useCallback((location: LatLng, locationName: string) => {
    setSelectedLocation(location);
    setEntryData(prev => ({ ...prev, locationName }));
    setDialogOpen(true);
  }, []);

  const handleMarkerClick = useCallback((entry: UniversalMapEntry) => {
    navigate(`/entry/${entry.id}`);
  }, [navigate]);

  const handleGetCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by this browser.');
      return;
    }

    setIsGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const location = { lat: latitude, lng: longitude };
        
        // Update map center and zoom to current location
        setCenter(location);
        setZoom(15); // Zoom in to show local area
        setSelectedLocation(location);
        setIsGettingLocation(false);
        
        // Automatically open the create memory dialog
        setDialogOpen(true);
      },
      (error) => {
        setIsGettingLocation(false);
        console.error('Error getting location:', error);
        switch(error.code) {
          case error.PERMISSION_DENIED:
            alert('Location access denied. Please enable location permissions.');
            break;
          case error.POSITION_UNAVAILABLE:
            alert('Location information is unavailable.');
            break;
          case error.TIMEOUT:
            alert('Location request timed out.');
            break;
          default:
            alert('An unknown error occurred while retrieving location.');
            break;
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      }
    );
  }, []);

  // Search functionality
  const [cityValue, setCityValue] = useState('');
  const [stateValue, setStateValue] = useState('');
  const [businessValue, setBusinessValue] = useState('');
  const [isSearchingLocation, setIsSearchingLocation] = useState(false);
  const [isSearchingBusiness, setIsSearchingBusiness] = useState(false);

  const handleLocationFilter = useCallback(async (city: string, state: string) => {
    if (!city.trim() && !state.trim()) return;

    setIsSearchingLocation(true);
    try {
      console.log('🔍 MapView: Searching for city/state:', city, state);
      
      // Build search query combining city and state
      const searchQuery = [city, state].filter(Boolean).join(', ');
      
      // Use our search provider API
      const response = await fetch('/api/search-providers/search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: searchQuery,
          type: 'address',
          city: city,
          state: state,
          limit: 5
        })
      });
      const data = await response.json();
      
      console.log('🔍 MapView: City/state search response:', data);
      
      if (data.success && data.results && data.results.length > 0) {
        const result = data.results[0];
        const location = {
          lat: result.latitude || result.lat,
          lng: result.longitude || result.lng
        };
        
        console.log('🔍 MapView: Found city/state location:', location, 'from provider:', result.provider);
        
        // Update map center to show the searched location
        setCenter(location);
        setZoom(12); // Good zoom level for city view
        
        if (handleLocationSearch) {
          const placeName = result.name || result.display_name || result.formatted_address || `${city}, ${state}`;
          handleLocationSearch(location, placeName);
        }
      } else {
        console.warn('🔍 MapView: No results found for city/state:', city, state);
      }
    } catch (error) {
      console.error('🔍 MapView: Error in city/state search:', error);
    } finally {
      setIsSearchingLocation(false);
    }
  }, [handleLocationSearch]);

  const handleBusinessSearch = useCallback(async (business: string, city: string, state: string) => {
    if (!business.trim()) return;

    setIsSearchingBusiness(true);
    try {
      console.log('🔍 MapView: Searching for business:', business, 'in', city, state);
      
      // Use our search provider API for business search
      const response = await fetch('/api/search-providers/search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: business,
          type: 'business',
          city: city,
          state: state,
          limit: 5
        })
      });
      const data = await response.json();
      
      console.log('🔍 MapView: Business search response:', data);
      
      if (data.success && data.results && data.results.length > 0) {
        const result = data.results[0];
        const location = {
          lat: result.latitude || result.lat,
          lng: result.longitude || result.lng
        };
        
        console.log('🔍 MapView: Found business location:', location, 'from provider:', result.provider);
        
        // Update map center to show the searched business
        setCenter(location);
        setZoom(15); // Closer zoom for specific business
        
        if (handleLocationSearch) {
          const placeName = result.name || result.display_name || result.formatted_address || business;
          handleLocationSearch(location, placeName);
        }
      } else {
        console.warn('🔍 MapView: No results found for business:', business);
      }
    } catch (error) {
      console.error('🔍 MapView: Error in business search:', error);
    } finally {
      setIsSearchingBusiness(false);
    }
  }, [handleLocationSearch]);

  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
    setSelectedLocation(null);
    setEntryData({
      locationName: '',
      entryDate: getTodayString(),
    });
  }, []);

  // Show loading state
  if (isLoading) {
    return (
      <Box sx={backgroundStyles.secondary}>
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Card sx={componentStyles.glassCard}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <CircularProgress size={60} />
              </Box>
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box sx={backgroundStyles.secondary}>
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            Error loading travel entries. Please try again later.
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={backgroundStyles.secondary}>
      <Fade in={true} timeout={800}>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
              Explore & Create Memories
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2, maxWidth: 600, mx: 'auto' }}>
              Search for locations to add new memories, or click on the map to start a new memory. View existing memories by clicking on their pins. Use the current location button <MyLocationIcon sx={{ fontSize: 16, verticalAlign: 'text-bottom', mx: 0.5 }} /> to create a new memory at your current location.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {entries.length > 0 && (
                <Typography component="span" sx={{ display: 'block', mt: 1 }}>
                  Showing {entries.length} travel {entries.length === 1 ? 'entry' : 'entries'}.
                </Typography>
              )}
            </Typography>
          </Box>

          {/* Search Fields */}
          <Card sx={{ ...componentStyles.glassCard, mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Search for new Memory Locations
              </Typography>
              
              {/* Business/POI Search */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom color="text.secondary">
                  Search for Businesses & Points of Interest
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '3fr 2fr 2fr 1fr 1fr 1fr' }, gap: 2, alignItems: 'end' }}>
                  <TextField
                    fullWidth
                    label="Business or Place"
                    placeholder="e.g., Starbucks, Disney World, Best Buy"
                    helperText="Restaurant, store, attraction, etc."
                    size="small"
                    value={businessValue}
                    onChange={(e) => setBusinessValue(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleBusinessSearch(businessValue, cityValue, stateValue);
                      }
                    }}
                  />
                  <TextField
                    fullWidth
                    label="City (Optional)"
                    placeholder="e.g., Orlando"
                    helperText="Narrow search to city"
                    size="small"
                    value={cityValue}
                    onChange={(e) => setCityValue(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        if (businessValue.trim()) {
                          handleBusinessSearch(businessValue, cityValue, stateValue);
                        } else {
                          handleLocationFilter(cityValue, stateValue);
                        }
                      }
                    }}
                  />
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>State (Optional)</InputLabel>
                      <Select
                        label="State (Optional)"
                        value={stateValue}
                        onChange={(e) => setStateValue(e.target.value as string)}
                      >
                        <MenuItem value="">
                          <em>Any State</em>
                        </MenuItem>
                        {US_STATES.map(state => (
                          <MenuItem key={state.value} value={state.value}>
                            {state.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Box sx={{ height: '20px' }} /> {/* Spacer to match TextField helper text height */}
                  </Box>
                  <Button
                    variant="contained"
                    onClick={() => handleBusinessSearch(businessValue, cityValue, stateValue)}
                    disabled={!businessValue.trim() || isSearchingBusiness}
                    size="medium"
                    sx={{ height: 40, alignSelf: 'start' }}
                    startIcon={isSearchingBusiness ? <CircularProgress size={16} /> : undefined}
                  >
                    {isSearchingBusiness ? 'Searching...' : 'Search'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => handleLocationFilter(cityValue, stateValue)}
                    disabled={(!cityValue.trim() && !stateValue) || isSearchingLocation}
                    size="medium"
                    sx={{ height: 40, alignSelf: 'start' }}
                    startIcon={isSearchingLocation ? <CircularProgress size={16} /> : undefined}
                  >
                    {isSearchingLocation ? 'Searching...' : 'Go to City'}
                  </Button>
                  <Button
                    variant="text"
                    onClick={() => {
                      setBusinessValue('');
                      setCityValue('');
                      setStateValue('');
                      setCenter({ lat: 39.8282, lng: -98.5795 }); // Reset to center of USA
                      setZoom(4);
                    }}
                    size="medium"
                    sx={{ height: 40, alignSelf: 'start' }}
                  >
                    Clear
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Universal Map Integration */}
          <Card sx={componentStyles.glassCard}>
            <CardContent>
              <Box sx={{ height: 500 }}>
                <UniversalMap
                  center={center}
                  zoom={zoom}
                  onMapClick={handleMapClick}
                  onLocationSearch={handleLocationSearch}
                  onMarkerClick={handleMarkerClick}
                  markers={markers}
                  entries={mapEntries}
                  showSearch={false}
                  showControls={true}
                  interactive={true}
                  style={{ height: '100%', width: '100%' }}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Current Location Floating Action Button */}
          <Fab
            color="primary"
            aria-label="get current location"
            onClick={handleGetCurrentLocation}
            disabled={isGettingLocation}
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: 1000,
            }}
          >
            {isGettingLocation ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              <MyLocationIcon />
            )}
          </Fab>

          {/* Entry Creation Dialog */}
          <CreateEntryDialog
            open={dialogOpen}
            onClose={handleDialogClose}
            initialLocation={
              selectedLocation
                ? {
                    latitude: selectedLocation.lat,
                    longitude: selectedLocation.lng,
                    locationName: entryData.locationName,
                  }
                : undefined
            }
            initialDate={entryData.entryDate}
          />
        </Container>
      </Fade>
    </Box>
  );
};

export default MapView;
